/**
 * Sedes — los puntos desde donde se opera y se factura.
 *
 * La empresa es una sola; las sedes son sus locales. Cada una lleva su propio
 * consecutivo de facturas y de órdenes, así que el número dice de dónde salió
 * el documento sin tener que abrirlo.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Stack, Alert, Tooltip, Switch,
  FormControlLabel, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../api';
import { CampoTelefono } from './CamposContacto';
import { errorTelefono } from '../contacto';
import { useEsMovil, FILA, ancho, tablaAncha, ENCABEZADO, botonEncabezado, rotulo } from '../estilos/responsivo';

const VACIA = { codigo: '', nombre: '', direccion: '', ciudad: '', telefono: '',
                responsable: '', prefijo_factura: '', prefijo_orden: '', activa: true };

export default function Sedes() {
  const esMovil = useEsMovil();
  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [edicion, setEdicion] = useState(null);
  const [errorForm, setErrorForm] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sedes', { params: { incluir_inactivas: true } });
      setSedes(data); setError('');
    } catch (e) { setError(e.response?.data?.detail || 'No se pudieron cargar las sedes'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    if (!edicion.codigo?.trim() || !edicion.nombre?.trim()) {
      setErrorForm('El código y el nombre son obligatorios.'); return;
    }
    if (errorTelefono(edicion.telefono)) { setErrorForm(errorTelefono(edicion.telefono)); return; }
    try {
      if (edicion.id) await api.put(`/sedes/${edicion.id}`, edicion);
      else await api.post('/sedes', edicion);
      setEdicion(null); setErrorForm(''); cargar();
    } catch (e) { setErrorForm(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  const borrar = async (s) => {
    if (!window.confirm(`¿Borrar la sede ${s.nombre}?`)) return;
    try { await api.delete(`/sedes/${s.id}`); cargar(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo borrar'); }
  };

  return (
    <Box>
      <Box sx={ENCABEZADO}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Cada sede lleva su propio consecutivo de facturas y de órdenes.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          {...botonEncabezado(esMovil)}
          onClick={() => { setEdicion({ ...VACIA }); setErrorForm(''); }}>
          {rotulo(esMovil, 'Nueva', 'Nueva sede')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card>
        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" sx={tablaAncha(820)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Código', 'Sede', 'Responsable', 'Facturas', 'Órdenes', 'Numeración',
                    'Estado', ''].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11,
                      textTransform: 'uppercase', color: '#64748b' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sedes.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Chip size="small" label={s.codigo} sx={{ fontWeight: 800 }} />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{s.nombre}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                        {[s.direccion, s.ciudad].filter(Boolean).join(' · ')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{s.responsable || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{s.facturas}</TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>
                      {s.ordenes}
                      {s.ordenes_abiertas > 0 && (
                        <Chip size="small" label={`${s.ordenes_abiertas} en curso`}
                          sx={{ ml: .75, height: 18, fontSize: 10,
                                bgcolor: '#e0f2fe', color: '#0369a1' }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11.5, fontFamily: 'ui-monospace, monospace' }}>
                      FV-{s.prefijo_factura}-{String((s.consecutivo_factura || 0) + 1).padStart(4, '0')}
                      <br />
                      OS-{s.prefijo_orden}-{String((s.consecutivo_orden || 0) + 1).padStart(4, '0')}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={s.activa ? 'Activa' : 'Inactiva'}
                        color={s.activa ? 'success' : 'default'}
                        variant={s.activa ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => { setEdicion({ ...s }); setErrorForm(''); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Borrar">
                        <IconButton size="small" color="error" onClick={() => borrar(s)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog fullScreen={esMovil} open={Boolean(edicion)} onClose={() => setEdicion(null)} fullWidth maxWidth="sm">
        <DialogTitle>{edicion?.id ? 'Editar sede' : 'Nueva sede'}</DialogTitle>
        <DialogContent dividers>
          {errorForm && <Alert severity="error" sx={{ mb: 2 }}>{errorForm}</Alert>}
          {edicion && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Código *" sx={ancho(130)}
                  value={edicion.codigo}
                  helperText="GAL, CHA…"
                  onChange={e => setEdicion({ ...edicion, codigo: e.target.value.toUpperCase() })} />
                <TextField size="small" label="Nombre *" fullWidth value={edicion.nombre}
                  onChange={e => setEdicion({ ...edicion, nombre: e.target.value })} />
              </Stack>
              <TextField size="small" label="Dirección" fullWidth value={edicion.direccion || ''}
                onChange={e => setEdicion({ ...edicion, direccion: e.target.value })} />
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Ciudad" fullWidth value={edicion.ciudad || ''}
                  onChange={e => setEdicion({ ...edicion, ciudad: e.target.value })} />
                <CampoTelefono etiqueta="Teléfono" fullWidth valor={edicion.telefono || ''}
                  alCambiar={(v) => setEdicion({ ...edicion, telefono: v })} />
              </Stack>
              <TextField size="small" label="Responsable" fullWidth value={edicion.responsable || ''}
                onChange={e => setEdicion({ ...edicion, responsable: e.target.value })} />
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Prefijo de factura" fullWidth
                  value={edicion.prefijo_factura || ''}
                  helperText="Vacío = se usa el código"
                  onChange={e => setEdicion({ ...edicion, prefijo_factura: e.target.value.toUpperCase() })} />
                <TextField size="small" label="Prefijo de orden" fullWidth
                  value={edicion.prefijo_orden || ''}
                  helperText="Vacío = se usa el código"
                  onChange={e => setEdicion({ ...edicion, prefijo_orden: e.target.value.toUpperCase() })} />
              </Stack>
              <FormControlLabel control={
                <Switch checked={Boolean(edicion.activa)}
                  onChange={e => setEdicion({ ...edicion, activa: e.target.checked })} />
              } label="Sede activa" />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEdicion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
